import React from 'react';
import { Alert, AlertTitle, Button, Box } from '@mui/material';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import ChatIcon from '@mui/icons-material/Chat';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'shared/contexts/AuthContext';
import { UserRole } from 'shared/types';
import { useCompanyPlanStatus } from 'shared/hooks/queries/useCompanyPlanStatus';

interface OverCapacityBannerProps {
  /** Company to evaluate. Defaults to the current user's active company. */
  companyId?: string | null;
  sx?: object;
}

/**
 * Persistent warning shown when a company's active seat usage exceeds its
 * plan's effective cap — surfaced to global roles browsing the company and
 * to the company's own company_admin. Renders nothing when not over capacity.
 */
const OverCapacityBanner: React.FC<OverCapacityBannerProps> = ({
  companyId,
  sx,
}) => {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const effectiveCompanyId = companyId ?? user?.company?._id ?? user?.companyId ?? null;
  const { overCapacity, effectiveSeats, effectiveCap } = useCompanyPlanStatus(effectiveCompanyId);

  if (!overCapacity || !effectiveCompanyId) return null;

  const isPlatformRole = hasRole(UserRole.SUPER_ADMIN) || hasRole(UserRole.SAFETY_STAFF);
  const isCompanyAdmin = hasRole(UserRole.COMPANY_ADMIN);

  return (
    <Alert severity="warning" sx={{ mb: 2, ...sx }}>
      <AlertTitle>Empresa sobre el cupo de su plan</AlertTitle>
      Esta empresa tiene {effectiveSeats} usuarios activos, por encima del cupo de{' '}
      {effectiveCap} que permite su plan actual. Reduzca la cantidad de usuarios activos
      o actualice el plan para regularizar el cupo.
      {isPlatformRole && (
        <Box sx={{ mt: 1.5 }}>
          <Button
            variant="contained"
            color="warning"
            size="small"
            startIcon={<EqualizerIcon />}
            onClick={() => navigate(`/companies/${effectiveCompanyId}/configuracion`)}
          >
            Ver plan y cupo
          </Button>
        </Box>
      )}
      {isCompanyAdmin && (
        <Box sx={{ mt: 1.5 }}>
          <Button
            variant="contained"
            color="warning"
            size="small"
            startIcon={<ChatIcon />}
            onClick={() => navigate('/settings')}
          >
            Ver plan y cupo
          </Button>
        </Box>
      )}
    </Alert>
  );
};

export default OverCapacityBanner;
